<template>
  <section
    v-if="iframeSrc"
    :class="[backgroundClasses, 'pb-0! lg:pb-12.5! lg:flex lg:flex-row-reverse']"
  >
    <div class="flex flex-col -mb-7 lg:min-w-4/12 lg:pl-30 lg:mb-0 lg:my-20">
      <h2
        v-if="content.title"
        class="text-4xl lg:text-6xl font-title uppercase mb-5"
        v-reveal="{ delay: 350 }"
      >
        {{ content.title }}
      </h2>
      <p
        v-if="content.description"
        v-reveal="{ delay: 450 }"
        class="whitespace-break-spaces mb-8.5"
      >
        {{ content.description }}
      </p>
      <div class="mb-5 flex gap-1.25">
        <div
          v-for="(_, idx) in content.locations"
          :key="`mobileMarker-${idx}`"
          :class="[
            'w-1.75 h-1.75 rounded-full bg-dustrose transition-opacity lg:hidden',
            { 'opacity-25': idx !== activeIndex },
          ]"
        />
      </div>
      <transition-group
        ref="listRef"
        @scroll.passive="onScroll"
        name="fade-out"
        tag="ul"
        class="w-dvw -translate-x-5 px-5 relative z-10 flex gap-5 overflow-x-scroll lg:w-auto lg:-ml-42 lg:translate-x-0 lg:mb-0 lg:px-0 lg:overflow-visible"
      >
        <VRLocationCard
          v-for="location in orderedLocations"
          :key="location.flexKey"
          :content="location"
          @click="setIframeSrc(location.link)"
        />
      </transition-group>
      <div class="hidden my-5 lg:flex gap-2.5 items-center">
        <div
          v-for="(_, idx) in content.locations"
          :key="`desktopMarker-${idx}`"
          :class="[
            'hidden w-6.25 h-1 bg-dustrose transition-opacity lg:block',
            { 'opacity-25': idx !== activeIndex },
          ]"
        />
        <div class="ml-auto hidden lg:flex gap-3">
          <ArrowButton :reverse="true" @click="prevDesktop" />
          <ArrowButton @click="nextDesktop" />
        </div>
      </div>
    </div>
    <iframe
      :src="iframeSrc"
      class="w-dvw h-[80dvh] -translate-x-5 border-0 lg:translate-x-0 lg:-ml-25 lg:w-8/12 lg:min-w-6/12 lg:max-w-2xl lg:h-auto"
      allowfullscreen="true"
      loading="lazy"
      referrerpolicy="no-referrer-when-downgrade"
    ></iframe>
  </section>
</template>
<script lang="ts">
import { type PropType, toRef } from 'vue'

import VRLocationCard from '@/components/cards/VRLocationCard.vue'
import ArrowButton from '@/components/small/ArrowButton.vue'
import { useBackgroundColor } from '@/composables/useBackgroundColor.ts'
export default {
  name: 'VisitVRSection',
  components: { VRLocationCard, ArrowButton },
  props: {
    content: {
      type: Object as PropType<VisitVRSection>,
      required: true,
    },
  },
  setup(props) {
    return {
      backgroundClasses: useBackgroundColor(toRef(props.content, 'background')),
    }
  },
  data() {
    return {
      iframeSrc: import.meta.env.VITE_APP_GEO_TOUR_URL || '',
      activeIndex: 0,
      startIndex: 0,
    }
  },
  computed: {
    orderedLocations(): any[] {
      const locs = this.content?.locations || []
      if (!locs.length) return []
      const n = this.startIndex % locs.length
      return locs.slice(n).concat(locs.slice(0, n))
    },
    locationCount(): number {
      return this.content?.locations?.length || 0
    },
  },
  methods: {
    onScroll() {
      this.findActiveIndex()
    },
    findActiveIndex() {
      const list = this.$refs.listRef as any

      if (!list) return

      const element = list.$el ?? list

      if (!element) return

      const maxScroll = element.scrollWidth - element.clientWidth

      if (maxScroll <= 0) {
        this.activeIndex = 0
        return
      }

      const scrollAmount = element.scrollLeft / maxScroll

      this.activeIndex = Math.floor(scrollAmount * (this.locationCount - 1))
    },
    nextDesktop() {
      if (!this.locationCount) return
      this.startIndex = ++this.startIndex % this.locationCount
      this.activeIndex = this.startIndex
    },
    prevDesktop() {
      if (!this.locationCount) return
      this.startIndex = (--this.startIndex + this.locationCount) % this.locationCount
      this.activeIndex = this.startIndex
    },

    setIframeSrc(newSrc: string) {
      this.iframeSrc = newSrc
    },
  },
  mounted() {
    this.$nextTick(() => this.findActiveIndex())
    window.addEventListener('resize', this.findActiveIndex)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.findActiveIndex)
  },
}
</script>
